import type { ReactElement } from "react";

import { useDispatch } from "react-redux";

import { showSuccessToast } from "app/actions/toast_actions";
import { translate } from "app/filters/i18nFilter";
import { StyledLeadsPageRoot } from "app/pages/sales-intelligence/common-components/layout";
import { useIsLeadsCriticalDataLoading } from "app/pages/sales-intelligence/modules/leads-search/hooks/useIsLeadsCriticalDataLoading";
import { useResetAllFilters } from "app/pages/sales-intelligence/modules/leads-search-filters/hooks/useResetAllFilters";
import { useCompaniesListData } from "app/pages/sales-intelligence/modules/lists/hooks/useCompaniesListData";
import { HiddenCompaniesToast } from "app/pages/sales-intelligence/pages/opportunity-list/components/HiddenCompaniesToast/HiddenCompaniesToast";
import useInvalidateOpportunityQueries from "app/pages/sales-intelligence/pages/opportunity-list/hooks/useInvalidateOpportunityQueries";
import { StaticListRecommendationsSidebarContainer } from "app/pages/sales-intelligence/sub-modules/opportunities/components/miscellaneous/RecommendationsSidebar";
import useCurrentStaticList from "app/pages/sales-intelligence/sub-modules/opportunities/hooks/useCurrentStaticList";
import type { OpportunityListType } from "app/pages/sales-intelligence/sub-modules/opportunities/types";
import { SidebarContainer, SidebarTrackingSectionName } from "app/pages/sales-intelligence/sub-modules/right-sidebar";
import { is } from "app/pages/sales-intelligence/utils/type-guards";

import { addDomainsFromRecommendationsAndReFetchThunk } from "../../store/effects";
import StaticListPageHeaderContainer from "../PageHeader/StaticListPageHeaderContainer";
import PageMainContent from "../PageMainContent/PageMainContent";

const StaticListContent = (): ReactElement => {
    const dispatch = useDispatch();

    const currentStaticList = useCurrentStaticList();
    const isLeadsCriticalDataLoading = useIsLeadsCriticalDataLoading();
    const invalidateOpportunityQueries = useInvalidateOpportunityQueries();
    const { getData: getListData } = useCompaniesListData();
    const resetAllFilters = useResetAllFilters();

    const handleOnAddRecommendationsToList = async (list: OpportunityListType, domains: readonly string[]) => {
        await dispatch(addDomainsFromRecommendationsAndReFetchThunk(list.opportunityListId, domains));
        await invalidateOpportunityQueries(list.opportunityListId);
        const listDomains = getListData().map((item) => item.domain);

        const hasMissingDomains = domains.some((domain) => !listDomains.includes(domain));

        if (hasMissingDomains) {
            dispatch(showSuccessToast(<HiddenCompaniesToast onReset={resetAllFilters} />));
        } else {
            const toastTextKey =
                domains.length > 1
                    ? "si.pages.search_result.add_leads_to_list.success.plural"
                    : "si.pages.search_result.add_leads_to_list.success";

            dispatch(showSuccessToast(translate(toastTextKey)));
        }
    };

    return (
        <StyledLeadsPageRoot>
            <StaticListPageHeaderContainer />
            <PageMainContent />

            {isLeadsCriticalDataLoading ? null : (
                <SidebarContainer trackingSectionName={SidebarTrackingSectionName.List} />
            )}

            {is.undefined(currentStaticList) ? null : (
                <StaticListRecommendationsSidebarContainer
                    list={currentStaticList}
                    onAddRecommendationToList={handleOnAddRecommendationsToList}
                />
            )}
        </StyledLeadsPageRoot>
    );
};

export default StaticListContent;
